import { buildTimestampAccessor } from '../features/metricValue';
import { applyWindow, getFirstWindowOrDefault } from '../features/window';
import { DataTable, EffectiveDefinition, throwIfUndefined } from '../models';
import { isTableCalcRef } from '../models/EffectiveDefinition';
import { Err, MetricsEngineError, Ok, Result } from '../models/Result';
import { DefaultTransformRegistry } from './DefaultTransformRegistry';
import { TransformFunction, TransformRegistry } from './transform';

/**
 * The top level data transformation function.
 *
 * Responsible for converting any calculated columns into concrete values,
 * preparing the table for visualization and interactivity.
 *
 * ### Interface
 * The interface is designed to be robust to significant changes in
 * implementation approach.
 *
 * If we decide to re-write this logic in a different language to support
 * execution on the server, or re-implement for performance reasons, this
 * interface should (hopefully) not have to change in any significant way.
 *
 * ### Implementation
 * At a high level, the current implementation is designed to fascilitate easily
 * adding new calculated column types, and is not particularly optimized for
 * performance.
 *
 * The definition is inspected, and converted into a series of {@link
 * TransformFunction}s. First, the `window` feature is applied, then calculated
 * columns.
 *
 * #### SIGNIFICANT LIMITATIONS:
 * The implementation does not currently support arbitrary composition of column
 * types.
 *
 * The two `table` transforms, {@link RowShift} and {@link TimeShif}
 * read from the `sourceTable` rather than the `previousStep`, which means they
 * can't reference any calculated columns.
 *
 * In future changes, we should remove these limitiations.
 *
 * @param definition The definition to materialize
 * @param sourceTable The source data to start with
 * @param registry The `TransformRegistry` to use for calculation lookup.
 * Defaults to `DefaultTransformRegistry`.
 * @returns The fully materialized table, or an error explaining why
 * materialization was unsuccessful
 */
export const materializeTable = (
  definition: EffectiveDefinition,
  sourceTable: DataTable,
  registry: TransformRegistry = DefaultTransformRegistry
): Result<DataTable, MetricsEngineError> => {
  try {
    const transforms = generateTransforms(definition, registry);
    return Ok(applyTransforms(transforms, sourceTable));
  } catch (e) {
    return Err(new MetricsEngineError(e?.message || 'Failed to materialize table'));
  }
};

const generateTransforms = (
  definition: EffectiveDefinition,
  registry: TransformRegistry
): ReadonlyArray<TransformFunction> => {
  const windowTransforms = generateWindowTransforms(definition);
  const columnTransforms = generateColumnTransforms(definition, registry);
  return windowTransforms.concat(columnTransforms);
};

const generateColumnTransforms = (
  definition: EffectiveDefinition,
  registry: TransformRegistry
): ReadonlyArray<TransformFunction> => {
  return Object.entries(definition.schema)
    .filter(([key, columnSchema]) => {
      return columnSchema.calculation !== undefined && !isTableCalcRef(columnSchema.calculation);
    })
    .map(([key, columnSchema]) => {
      const calcType = columnSchema.calculation?.type;
      const creator = calcType ? registry[calcType] : undefined;
      if (creator === undefined) {
        throw new Error(`Couldn't find transform creator in registry: ${calcType}, ${Object.keys(registry)}`);
      }

      return creator(definition, key);
    });
};

const generateWindowTransforms = (definition: EffectiveDefinition): ReadonlyArray<TransformFunction> => {
  const window = getFirstWindowOrDefault(definition);
  const accessor = buildTimestampAccessor(definition);
  return [(sourceTable, previousStep) => applyWindow(previousStep, window, throwIfUndefined(accessor))];
};

const applyTransforms = (transforms: ReadonlyArray<TransformFunction>, sourceTable: DataTable): DataTable => {
  return transforms.reduce(
    (previousStep: DataTable, transform: TransformFunction) => transform(sourceTable, previousStep),
    sourceTable
  );
};
